import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, 
  ChevronRight, 
  ShoppingCart, 
  Clock, 
  CheckCircle, 
  Truck, 
  AlertCircle,
  Loader2,
  RefreshCw,
  Calendar,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { ordersAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { 
      label: 'Pending', 
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: Clock 
    },
    processing: { 
      label: 'Processing', 
      className: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: RefreshCw 
    },
    shipped: { 
      label: 'Shipped', 
      className: 'bg-purple-100 text-purple-800 border-purple-200',
      icon: Truck 
    },
    delivered: { 
      label: 'Delivered', 
      className: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle 
    },
    cancelled: { 
      label: 'Cancelled', 
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: AlertCircle 
    },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

const MyOrdersPage = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/my-orders' } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.user_id) return;
      
      try {
        setLoading(true);
        const response = await ordersAPI.getUserOrders(user.user_id);
        setOrders(response.orders || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load your orders. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.user_id) {
      fetchOrders();
    }
  }, [user]);

  const handleReorder = async (order) => {
    setReorderingId(order.order_id);
    
    try {
      let addedCount = 0;
      
      for (const item of order.items) {
        // Create a product object from the order item
        const product = {
          id: item.product_id,
          name: item.product_name,
          image: item.image || 'https://placehold.co/200x200/f5f3f0/9a8b7d?text=Product',
          basePrice: item.unit_price,
          section: item.section || 'smallformat',
          priceType: 'fixed'
        };
        
        // Create options object from the item
        const selectedOptions = {
          size: item.size || 'Standard',
          finish: item.finish || 'Standard',
          stock: item.stock || 'Standard',
          quantity: item.quantity,
          ...item.options
        };
        
        addToCart(product, selectedOptions, item.quantity);
        addedCount++;
      }
      
      toast({
        title: "Items added to cart!",
        description: `${addedCount} item${addedCount > 1 ? 's' : ''} from order #${order.order_id} added to your cart.`,
        duration: 4000,
      });
      
      // Navigate to cart after a short delay
      setTimeout(() => {
        navigate('/cart');
      }, 500);
      
    } catch (err) {
      console.error('Error reordering:', err);
      toast({
        title: "Reorder failed",
        description: "Some items could not be added to your cart. Please try again.",
        variant: "destructive",
        duration: 4000,
      });
    } finally {
      setReorderingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold-600 animate-spin mx-auto mb-4" />
          <p className="text-ivory-500 font-body">Loading your orders...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - will redirect
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Header */}
      <div className="relative gradient-hero py-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800" data-testid="my-orders-heading">
            My Orders
          </h1>
          <p className="text-ivory-500 mt-2 font-body">View your order history and quickly reorder past purchases</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-5xl">
        {/* Error State */}
        {error && (
          <Card className="bg-red-50 border border-red-200 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 font-body">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!error && orders.length === 0 && (
          <Card className="bg-ivory-100 border border-ivory-300/50">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-xl bg-gold-500/10 flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-gold-600" />
              </div>
              <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-3">No orders yet</h2>
              <p className="text-ivory-500 mb-6 font-body">Start shopping to see your orders here!</p>
              <Button 
                size="lg" 
                asChild 
                className="btn-luxury-primary rounded-lg font-body"
              >
                <Link to="/products" data-testid="start-shopping-btn">Start Shopping</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Orders List */}
        {orders.length > 0 && (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card 
                key={order.order_id} 
                className="bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury hover:shadow-luxury-lg transition-shadow"
                data-testid={`order-card-${order.order_id}`}
              >
                <CardContent className="p-0">
                  {/* Order Header */}
                  <div className="p-4 sm:p-6 border-b border-ivory-200 bg-ivory-50/50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-heading font-semibold text-ivory-800">
                            Order #{order.order_id}
                          </h3>
                          <StatusBadge status={order.status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-ivory-500 font-body">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            {formatDate(order.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <DollarSign className="w-4 h-4" />
                            ${order.total?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="btn-luxury-outline rounded-lg font-body"
                          onClick={() => handleReorder(order)}
                          disabled={reorderingId === order.order_id}
                          data-testid={`reorder-btn-${order.order_id}`}
                        >
                          {reorderingId === order.order_id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Quick Reorder
                            </>
                          )}
                        </Button>
                        <Link 
                          to={`/my-orders/${order.order_id}`}
                          className="text-gold-600 hover:text-gold-700 font-body text-sm flex items-center gap-1"
                          data-testid={`view-order-btn-${order.order_id}`}
                        >
                          View Order
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Items */}
                  <div className="p-4 sm:p-6">
                    <div className="space-y-4">
                      {order.items?.slice(0, 3).map((item, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-4"
                        >
                          <div className="w-16 h-16 bg-ivory-200 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={item.image || 'https://placehold.co/100x100/f5f3f0/9a8b7d?text=Product'}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'https://placehold.co/100x100/f5f3f0/9a8b7d?text=Product';
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-body font-medium text-ivory-800 truncate">
                              {item.product_name}
                            </h4>
                            <p className="text-sm text-ivory-500 font-body">
                              Qty: {item.quantity} × ${item.unit_price?.toFixed(2) || '0.00'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-ivory-800 font-body">
                              ${((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {/* Show more items indicator */}
                      {order.items?.length > 3 && (
                        <p className="text-sm text-ivory-500 font-body pl-20">
                          + {order.items.length - 3} more item{order.items.length - 3 > 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Order Footer - Summary */}
                  <div className="px-4 sm:px-6 py-4 border-t border-ivory-200 bg-ivory-50/30">
                    <div className="flex justify-between items-center">
                      <span className="text-ivory-500 font-body">
                        {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      <div className="text-right">
                        <span className="text-sm text-ivory-500 font-body mr-2">Order Total:</span>
                        <span className="font-heading font-semibold text-lg text-gold-600">
                          ${order.total?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Help Section */}
        <Card className="bg-ivory-100 border border-ivory-300/50 mt-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="w-6 h-6 text-gold-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-ivory-800 mb-1">Need help with an order?</h3>
                <p className="text-ivory-500 font-body text-sm">
                  Contact our support team at{' '}
                  <a href="mailto:support@engraveca.com" className="text-gold-600 hover:text-gold-700">
                    support@engraveca.com
                  </a>{' '}
                  or call{' '}
                  <a href="tel:+18005210527" className="text-gold-600 hover:text-gold-700">
                    +1 800 521 0527
                  </a>
                </p>
              </div>
              <Button
                variant="outline"
                className="btn-luxury-outline rounded-lg font-body"
                asChild
              >
                <Link to="/contact">Contact Support</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyOrdersPage;
